import React, { useMemo } from 'react';
import * as THREE from 'three';
import { Vector3 } from 'three';

// Custom kidney bean geometry by indenting a sphere on the medial side
function KidneyBeanGeometry({ radius = 0.16, widthSegments = 32, heightSegments = 24, indent = 0.08, side = 'left' }) {
  const geom = useMemo(() => {
    const g = new THREE.SphereGeometry(radius, widthSegments, heightSegments);
    const pos = g.attributes.position;
    const s = side === 'left' ? 1 : -1; // medial notch orientation
    const a = radius * 0.8; // notch width control
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const z = pos.getZ(i);
      const r2 = (y * y) / (2 * a * a) + (z * z) / (2 * a * a);
      const notch = indent * Math.exp(-r2);
      pos.setX(i, x - s * notch);
    }
    pos.needsUpdate = true;
    g.computeVertexNormals();
    g.computeBoundingSphere();
    return g;
  }, [radius, widthSegments, heightSegments, indent, side]);
  return <primitive attach="geometry" object={geom} />;
}

// Liver geometry: flattened ellipsoid with right-lobe dominance and a medial cleft
function LiverGeometry({ radius = 0.27, widthSegments = 48, heightSegments = 36, indent = 0.08, orientation = 'right' }) {
  const geom = useMemo(() => {
    const g = new THREE.SphereGeometry(radius, widthSegments, heightSegments);
    const pos = g.attributes.position;
    const r = radius;
    const s = orientation === 'right' ? 1 : -1; // dominant lobe side
    const notchCenter = 0.12 * r * s; // slight offset into dominant lobe
    const a = 0.35 * r;           // notch width across X
    const by = 0.6 * r;           // notch height across Y
    for (let i = 0; i < pos.count; i++) {
      let x = pos.getX(i);
      let y = pos.getY(i);
      let z = pos.getZ(i);

      // Flatten vertical thickness and slightly compress depth
      y *= 0.55 * (1 + 0.15 * Math.max(0, (s * x) / r)); // dominant lobe hangs a touch lower
      z *= 0.75;

      // Emphasize right lobe volume and taper left lobe
      const domBoost = 1 + 0.35 * Math.max(0, (s * x) / r);
      const oppTaper = 1 - 0.20 * Math.max(0, (-s * x) / r);
      const sideScale = (s * x) >= 0 ? domBoost : oppTaper;
      x *= sideScale;

      // Medial cleft between lobes (falciform) — carve near midline on right side
      const notch = indent * Math.exp(-(((x - notchCenter) ** 2) / (2 * a * a) + (y * y) / (2 * by * by)));
      x -= notch; // push inward to create cleft

      // Triangular corner (previously applied on the opposite side) disabled per feedback

      pos.setXYZ(i, x, y, z);
    }
    pos.needsUpdate = true;
    g.computeVertexNormals();
    g.computeBoundingSphere();
    return g;
  }, [radius, widthSegments, heightSegments, indent]);
  return <primitive attach="geometry" object={geom} />;
}

// Stomach geometry: flattened, curved sac with a medial (lesser) curvature
function StomachGeometry({ radius = 0.22, widthSegments = 48, heightSegments = 32, indent = 0.08, side = 'left' }) {
  const geom = useMemo(() => {
    const g = new THREE.SphereGeometry(radius, widthSegments, heightSegments);
    const pos = g.attributes.position;
    const r = radius;
    const s = side === 'left' ? 1 : -1; // which side is lateral (fundus)
    const a = 0.35 * r;                 // groove width across X
    const by = 0.55 * r;                // groove height across Y
    for (let i = 0; i < pos.count; i++) {
      let x = pos.getX(i);
      let y = pos.getY(i);
      let z = pos.getZ(i);

      // Overall stomach proportions
      x *= 1.15;          // elongated horizontally
      y *= 0.50;          // flattened vertically
      z *= 0.70;          // shallower depth

      // Make fundus (lateral dome) fuller and slightly higher
      const lat = (s * x) / r;
      const latBoost = 1 + 0.25 * Math.max(0, lat);
      x *= latBoost;
      y += 0.06 * r * Math.max(0, lat);

      // Lesser curvature groove on medial border (toward midline)
      const medialSign = -s; // medial is opposite of lateral
      const grooveCenterX = medialSign * 0.04 * r;
      const notch = indent * Math.exp(-(((x - grooveCenterX) ** 2) / (2 * a * a) + (y * y) / (2 * by * by)));
      x -= medialSign * notch;

      // Taper toward the pylorus (medial/inferior end)
      const pylorusWeight = Math.max(0, (0.15 * r - (s * x)) / (0.4 * r));
      y -= 0.05 * r * pylorusWeight;
      z *= (1 - 0.15 * pylorusWeight);

      pos.setXYZ(i, x, y, z);
    }
    pos.needsUpdate = true;
    g.computeVertexNormals();
    g.computeBoundingSphere();
    return g;
  }, [radius, widthSegments, heightSegments, indent, side]);
  return <primitive attach="geometry" object={geom} />;
}


// Rounded container for the bowel mass (rounded rectangular prism via extrude)
function BowelContainerGeometry({ width = 0.80, height = 0.46, radius = 0.12, depth = 0.09, segments = 8, edgeRadius = 0.02, bevelSegments = 4 }) {
  const geom = useMemo(() => {
    const w = width, h = height, r = Math.min(radius, Math.min(w, h) * 0.45);
    const shape = new THREE.Shape();
    shape.moveTo(-w/2 + r, -h/2);
    shape.lineTo(w/2 - r, -h/2);
    shape.quadraticCurveTo(w/2, -h/2, w/2, -h/2 + r);
    shape.lineTo(w/2, h/2 - r);
    shape.quadraticCurveTo(w/2, h/2, w/2 - r, h/2);
    shape.lineTo(-w/2 + r, h/2);
    shape.quadraticCurveTo(-w/2, h/2, -w/2, h/2 - r);
    shape.lineTo(-w/2, -h/2 + r);
    shape.quadraticCurveTo(-w/2, -h/2, -w/2 + r, -h/2);
    const bevelT = Math.min(edgeRadius, depth * 0.48);
    const g = new THREE.ExtrudeGeometry(shape, {
      steps: 1,
      depth,
      curveSegments: Math.max(segments, 4),
      bevelEnabled: true,
      bevelSegments: Math.max(2, bevelSegments),
      bevelThickness: bevelT,
      bevelSize: edgeRadius,
      bevelOffset: 0
    });
    // Center along Z taking bevel into account
    g.computeBoundingBox();
    const bb = g.boundingBox;
    const centerZ = (bb.min.z + bb.max.z) / 2;
    g.translate(0, 0, -centerZ);
    g.computeVertexNormals();
    g.computeBoundingSphere();
    return g;
  }, [width, height, radius, depth, segments, edgeRadius, bevelSegments]);
  return <primitive attach="geometry" object={geom} />;
}

// Organ configuration data
export const organsConfig = [
  {
    id: 'heart',
    label: 'Heart',
    position: [0, 6.5, 0],
    geometry: <sphereGeometry args={[0.12, 20, 16]} />,
    labelOffset: [0.5, 0.55, 0],
    scale: [1.3, 1.4, 1], // Slightly thicker egg-like shape
  },
  {
    id: 'brain',
    label: 'Brain',
    position: [0, 7.6, -0.05],
    geometry: <sphereGeometry args={[0.26, 10, 10]} />,
    labelOffset: [0.4, 0.3, 0],
    scale: 1,
  },

  {
    id: 'lungs',
    label: 'Lungs',
    position: [-0.25, 6.2, 0],
    geometry: <sphereGeometry args={[0.3, 16, 16]} />,
    labelOffset: [0.6, 0.4, 0],
    scale: [0.85, 1.5, 1], // Elongated lung shape
    rotation: [0, 0, -0.1], // Tilt left lung base towards center
    // Special case for lungs - they have two identical lobes
    extra: (
      <mesh position={[0.25, 6.2, 0]} scale={[0.85, 1.5, 1]} rotation={[0, 0, 0.1]}>
        <sphereGeometry args={[0.3, 16, 16]} />
        <meshPhysicalMaterial
          roughness={0.1}
          transmission={1}
          thickness={0.5}
          color="#ffffff"
          transparent
          opacity={0.25}
        />
      </mesh>
    ),
  },


  {
    id: 'liver',
    label: 'Liver',
    // Right upper quadrant, just inferior to lungs; aligned with torso
    position: [-0.15, 5.65, 0.1],
    // Custom liver geometry: bilobed with medial cleft and flattened inferior surface
    geometry: <LiverGeometry radius={0.28} widthSegments={100} heightSegments={70} />,
    labelOffset: [0.58, 0.10, 0],
    scale: 1.05,
    rotation: [1.5, 0.25, 0.02],
  },
  {
    id: 'stomach',
    label: 'Stomach',
    // Left upper quadrant, just inferior to the left lung, medial curvature faces midline
    position: [0.1, 5.5, 0.1],
    geometry: <StomachGeometry radius={0.23} widthSegments={56} heightSegments={36} indent={0.09} side="left" />,
    labelOffset: [0.48, 0.14, 0],
    scale: 1.1,
    rotation: [1.5, 0.40, -0.2],
  },
  {
    id: 'bowel',
    label: 'Bowel',
    // Container centered in lower abdomen
    position: [0.0, 5.32, -0.06],
    geometry: <BowelContainerGeometry width={0.75} height={0.64} radius={0.24} depth={0.28} segments={20} />,
    labelOffset: [0.55, 0.18, 0],
    scale: 1.06,
    rotation: [0.0, -0.02, 0.0],
    // Render encasing colon + internal small bowel coils
 
  },
  // removed: combined into single 'bowel' entry with container + colon + small bowel
  {
    id: 'kidneys',
    label: 'Kidneys',
    // Retroperitoneal, either side of the spine; moved slightly inferior and closer together
    // Base mesh now represents the RIGHT kidney (left will be rendered as `extra`)
    position: [0.3, 5.6, -0.06],
    // Actual bean-like geometry with medial indentation
    geometry: <KidneyBeanGeometry radius={0.17} widthSegments={36} heightSegments={28} indent={0.085} side="right" />,
    labelOffset: [0.5, 0.2, 0],
    scale: [0.65, 0.95, 0.6],
    rotation: [0.02, 0.15, -0.12],
    // Render the contralateral kidney explicitly with matching material
    extra: (
      <mesh position={[-0.3, 5.6, -0.06]} scale={[0.65, 0.95, 0.6]} rotation={[0.02, -0.15, 0.12]}>
        <KidneyBeanGeometry radius={0.17} widthSegments={36} heightSegments={28} indent={0.085} side="left" />
        <meshPhysicalMaterial
          roughness={0.1}
          transmission={1}
          thickness={0.5}
          color="#ffffff"
          transparent
          opacity={0.25}
        />
      </mesh>
    ),
  },
];

// Helper function to get organ by ID
export const getOrganById = (id) => {
  return organsConfig.find(organ => organ.id === id);
};

// Helper function to get all organ IDs
export const getAllOrganIds = () => {
  return organsConfig.map(organ => organ.id);
};

// Helper function to calculate label position
export const calculateLabelPosition = (organ) => {
  return new Vector3(...organ.position).add(new Vector3(...organ.labelOffset));
}; 
